import { useState } from 'react';
import { Link } from 'react-router';
import { EmptyState, PageHeader } from '../components/ui.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { GROUPS, useNavEntries, usePref, type NavEntry, type NavGroup } from '../components/nav.tsx';
import { Shell } from '../components/Shell.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';

/**
 * Every application the person may open, as tiles in seven families, with a filter and their
 * favourites first. It reads the same list as the side menu, so it never shows a place the
 * person cannot open.
 */

export const FAMILIES: { key: string; label: MessageKey; hint: MessageKey; icon: IconName; groups: NavGroup[] }[] = [
  { key: 'business', label: 'lnc.business', hint: 'lnc.business_hint', icon: 'layers', groups: ['projects', 'documents', 'collab', 'reports'] },
  { key: 'sales', label: 'lnc.sales', hint: 'lnc.sales_hint', icon: 'trendUp', groups: ['crm', 'sales'] },
  { key: 'finance', label: 'lnc.finance', hint: 'lnc.finance_hint', icon: 'bank', groups: ['finance'] },
  { key: 'hr', label: 'lnc.hr', hint: 'lnc.hr_hint', icon: 'contact', groups: ['hr'] },
  { key: 'operations', label: 'lnc.operations', hint: 'lnc.operations_hint', icon: 'factory', groups: ['purchases', 'inventory', 'operations'] },
  { key: 'industry', label: 'lnc.industry', hint: 'lnc.industry_hint', icon: 'store', groups: ['industry'] },
  { key: 'admin', label: 'lnc.admin', hint: 'lnc.admin_hint', icon: 'settings', groups: ['settings'] },
];

const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export function Apps() {
  const { entries } = useNavEntries();
  const [favs, setFavs] = usePref<string[]>('nav.fav', []);
  const [q, setQ] = useState('');
  const groupLabel = (g: NavGroup) => t(GROUPS.find((x) => x.key === g)!.label);
  const words = fold(q).split(/\s+/).filter(Boolean);
  const shown = entries.filter((e) => words.every((w) => fold(`${e.label} ${groupLabel(e.group)}`).includes(w)));
  const toggle = (to: string) => setFavs(favs.includes(to) ? favs.filter((x) => x !== to) : [...favs, to]);
  const favEntries = favs.map((to) => shown.find((e) => e.to === to)).filter((e): e is NavEntry => !!e);

  const tile = (e: NavEntry, key = e.to) => (
    <li key={key} className="app-tile">
      <Link to={e.to} className="app-tile-link">
        <span className="app-tile-icon" aria-hidden="true">
          <Icon name={e.icon} size={22} />
        </span>
        <span className="app-tile-text">
          <span className="app-tile-name">{e.label}</span>
          <span className="app-tile-group">{groupLabel(e.group)}</span>
        </span>
        {e.badge ? (
          <span className="nav-badge app-tile-badge" aria-label={e.badgeLabel}>
            {e.badge > 99 ? '99+' : e.badge}
          </span>
        ) : null}
      </Link>
      <button
        type="button"
        className={`app-tile-star${favs.includes(e.to) ? ' on' : ''}`}
        aria-pressed={favs.includes(e.to)}
        aria-label={favs.includes(e.to) ? t('shell.unfavourite', { name: e.label }) : t('shell.favourite', { name: e.label })}
        title={favs.includes(e.to) ? t('shell.unfavourite', { name: e.label }) : t('shell.favourite', { name: e.label })}
        onClick={() => toggle(e.to)}
      >
        <Icon name="star" size={16} />
      </button>
    </li>
  );

  return (
    <Shell>
      <div className="page apps-page">
        <PageHeader title={t('lnc.title')} subtitle={tn('lnc.subtitle', entries.length)}>
          <div className="search apps-search">
            <Icon name="search" size={18} />
            <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('lnc.filter')} aria-label={t('lnc.filter')} />
          </div>
        </PageHeader>
        {!shown.length ? <EmptyState icon="search" title={t('lnc.none', { q })} body={t('lnc.none_hint')} /> : null}
        {favEntries.length ? (
          <section className="apps-family" aria-labelledby="apps-fav">
            <h2 id="apps-fav" className="apps-family-title">
              <Icon name="star" size={18} />
              {t('shell.favourites')}
            </h2>
            <ul className="apps-grid">{favEntries.map((e) => tile(e, `f${e.to}`))}</ul>
          </section>
        ) : null}
        {FAMILIES.map((f) => {
          const items = shown.filter((e) => f.groups.includes(e.group));
          if (!items.length) return null;
          return (
            <section key={f.key} className="apps-family" aria-labelledby={`apps-${f.key}`}>
              <h2 id={`apps-${f.key}`} className="apps-family-title">
                <Icon name={f.icon} size={18} />
                {t(f.label)}
                <span className="apps-family-hint">{t(f.hint)}</span>
              </h2>
              <ul className="apps-grid">{items.map((e) => tile(e))}</ul>
            </section>
          );
        })}
      </div>
    </Shell>
  );
}
